var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useAuth0 } from "@auth0/auth0-react";
import React, { createContext, useContext } from "react";
import config from "../config";
const AuthContext = createContext(undefined);
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
export const AuthProvider = ({ children }) => {
    const { user: auth0User, isLoading: auth0Loading, error: auth0Error, loginWithRedirect, logout, getAccessTokenSilently, } = useAuth0();
    const [token, setToken] = React.useState(null);
    const [user, setUser] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    React.useEffect(() => {
        const fetchUserData = () => __awaiter(void 0, void 0, void 0, function* () {
            if (auth0User && !auth0Loading) {
                try {
                    let accessToken;
                    try {
                        accessToken = yield getAccessTokenSilently();
                    }
                    catch (tokenErr) {
                        const code = tokenErr === null || tokenErr === void 0 ? void 0 : tokenErr.error;
                        if (code === "consent_required" || code === "login_required") {
                            yield loginWithRedirect({ authorizationParams: { prompt: "consent" } });
                            return;
                        }
                        throw tokenErr;
                    }
                    setToken(accessToken);
                    const response = yield fetch(`${config.API_URL}/user/self`, {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            "Content-Type": "application/json",
                        },
                    });
                    if (!response.ok) {
                        throw new Error(`Failed to fetch user: ${response.statusText}`);
                    }
                    const userData = yield response.json();
                    setUser(userData);
                    setError(null);
                }
                catch (err) {
                    console.error("Failed to get user data:", err);
                    setError(err instanceof Error ? err.message : "Failed to load user");
                }
                finally {
                    setLoading(false);
                }
            }
            else if (!auth0Loading) {
                setLoading(false);
            }
        });
        fetchUserData();
    }, [auth0User, auth0Loading, getAccessTokenSilently, loginWithRedirect]);
    const value = {
        user,
        token,
        loading: auth0Loading || loading,
        error: error || (auth0Error === null || auth0Error === void 0 ? void 0 : auth0Error.message) || null,
        loginWithRedirect,
        logout: logout,
    };
    return _jsx(AuthContext.Provider, Object.assign({ value: value }, { children: children }));
};
